import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, PackageOpen } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import LocationCombobox from "../components/LocationCombobox";
import CheckoutSummary from "../components/CheckoutSummary";
import { useApp } from "../context/AppContext";
import SEOHead from "../components/SEOHead";
import { checkoutApi } from "../lib/api";
import { initMetaPixel, trackEvent, getGlobalPixelId } from "../lib/metaPixel";

const CheckoutShippingPage = () => {
  const { shipping, setShipping, cartItems } = useApp();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [locations, setLocations] = useState({
    provinces: [],
    cities: [],
    districts: [],
    subdistricts: [],
  });
  const [loadingLocation, setLoadingLocation] = useState("");

  // Fire InitiateCheckout pixel event
  useEffect(() => {
    // Init pixel: try global env first, fallback to per-LP pixel that's already loaded
    const pixelId = getGlobalPixelId();
    if (pixelId) {
      initMetaPixel(pixelId);
    }
    // Always track — pixel is already initialized from LP page visit in this session
    // Use setTimeout to ensure fbq script is fully loaded
    const timer = setTimeout(() => {
      trackEvent("InitiateCheckout", {
        num_items: cartItems?.length || 0,
      });
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingLocation("province");
        const data = await checkoutApi.getProvinces();
        setLocations((current) => ({ ...current, provinces: data || [] }));
      } catch (error) {
        toast.error(error.message || "Gagal memuat provinsi RajaOngkir");
      } finally {
        setLoadingLocation("");
      }
    };
    load();
  }, []);

  const set = (k, v) => {
    setShipping((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const pickLocation = async (level, id, item) => {
    try {
      if (level === "province") {
        setShipping((current) => ({
          ...current,
          province_id: id,
          province: item?.name || "",
          city_id: "",
          district_id: "",
          subdistrict_id: "",
          kota: "",
          kecamatan: "",
          kelurahan: "",
          zip_code: "",
        }));
        setLocations((current) => ({ ...current, cities: [], districts: [], subdistricts: [] }));
        setLoadingLocation("city");
        const data = await checkoutApi.getCities({ province_id: id });
        setLocations((current) => ({ ...current, cities: data || [] }));
      }
      if (level === "city") {
        setShipping((current) => ({
          ...current,
          city_id: id,
          kota: item?.name || "",
          district_id: "",
          subdistrict_id: "",
          kecamatan: "",
          kelurahan: "",
          zip_code: "",
        }));
        setLocations((current) => ({ ...current, districts: [], subdistricts: [] }));
        setLoadingLocation("district");
        const data = await checkoutApi.getDistricts({ city_id: id });
        setLocations((current) => ({ ...current, districts: data || [] }));
      }
      if (level === "district") {
        setShipping((current) => ({
          ...current,
          district_id: id,
          kecamatan: item?.name || "",
          subdistrict_id: "",
          kelurahan: "",
          zip_code: "",
        }));
        setLocations((current) => ({ ...current, subdistricts: [] }));
        setLoadingLocation("subdistrict");
        const data = await checkoutApi.getSubdistricts({ district_id: id });
        setLocations((current) => ({ ...current, subdistricts: data || [] }));
      }
      if (level === "subdistrict") {
        setShipping((current) => ({
          ...current,
          subdistrict_id: id,
          kelurahan: item?.name || "",
          zip_code: item?.zip_code || current.zip_code || "",
        }));
      }
      setErrors((current) => ({ ...current, [level]: undefined }));
    } catch (error) {
      toast.error(error.message || "Gagal memuat data wilayah");
    } finally {
      setLoadingLocation("");
    }
  };

  if (cartItems.length === 0) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="shipping-empty">
        <PackageOpen className="w-16 h-16 mx-auto text-stone-300 mb-3" />
        <h1 className="font-display text-3xl font-semibold text-stone-900 mb-2">
          Keranjang Anda kosong
        </h1>
        <p className="text-stone-600 mb-6">
          Silakan pilih produk herbal terlebih dahulu sebelum mengisi data pengiriman.
        </p>
        <Link
          to="/"
          className="inline-flex items-center h-12 px-5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Pilih Produk
        </Link>
      </main>
    );
  }

  const validate = () => {
    const e = {};
    if (!shipping.name.trim()) e.name = "Nama wajib diisi";
    if (!/^[0-9+\s-]{8,16}$/.test(shipping.phone.trim()))
      e.phone = "Nomor HP/WA tidak valid (8-16 digit)";
    if (!shipping.address.trim()) e.address = "Alamat wajib diisi";
    if (!shipping.province_id) e.province = "Provinsi wajib dipilih";
    if (!shipping.city_id) e.city = "Kota wajib dipilih";
    if (!shipping.district_id) e.district = "Kecamatan wajib dipilih";
    if (!shipping.subdistrict_id) e.subdistrict = "Kelurahan wajib dipilih";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Mohon lengkapi data pengiriman Anda");
      return;
    }
    navigate("/checkout/pembayaran");
  };

  const fieldClass = "h-14 text-base rounded-xl border-stone-200 focus-visible:ring-emerald-500";
  const errClass = "text-sm text-rose-600 mt-1";

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="shipping-page">
      <SEOHead
        title="Checkout Pengiriman"
        description="Halaman checkout pengiriman Teras Alami untuk melengkapi alamat dan data penerima pesanan."
        path="/checkout/pengiriman"
        noIndex
      />

      <Link
        to="/"
        className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-6"
        data-testid="shipping-back"
      >
        <ArrowLeft className="w-4 h-4 mr-2" /> Kembali ke Beranda
      </Link>

      {/* Progress */}
      <div className="flex items-center gap-3 mb-8 text-sm">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-600 text-white font-semibold">
          1
        </span>
        <span className="font-medium text-stone-900">Data Pengiriman</span>
        <span className="h-px flex-1 bg-stone-200"></span>
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-stone-100 text-stone-500 font-semibold">
          2
        </span>
        <span className="text-stone-500">Pengiriman & Pembayaran</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
        <form
          onSubmit={onSubmit}
          className="lg:col-span-2 space-y-6"
          data-testid="shipping-form"
        >
          <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900">
              Data Pengiriman
            </h1>
            <p className="text-stone-600 mt-1 mb-6 text-base">
              Pastikan alamat dan nomor HP Anda lengkap agar pesanan sampai dengan aman.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <Label htmlFor="name" className="text-base text-stone-700">
                  Nama Lengkap
                </Label>
                <Input
                  id="name"
                  className={fieldClass}
                  value={shipping.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Contoh: Ibu Siti Aminah"
                  data-testid="input-name"
                />
                {errors.name && <p className={errClass}>{errors.name}</p>}
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="phone" className="text-base text-stone-700">
                  Nomor HP / WhatsApp
                </Label>
                <Input
                  id="phone"
                  className={fieldClass}
                  value={shipping.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="08xxxxxxxxxx"
                  inputMode="tel"
                  data-testid="input-phone"
                />
                {errors.phone && <p className={errClass}>{errors.phone}</p>}
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="address" className="text-base text-stone-700">
                  Alamat Lengkap
                </Label>
                <Textarea
                  id="address"
                  className="min-h-[100px] text-base rounded-xl border-stone-200 focus-visible:ring-emerald-500"
                  value={shipping.address}
                  onChange={(e) => set("address", e.target.value)}
                  placeholder="Jalan, nomor rumah, RT/RW, patokan"
                  data-testid="input-address"
                />
                {errors.address && <p className={errClass}>{errors.address}</p>}
              </div>

              <div>
                <Label className="text-base text-stone-700">
                  Provinsi
                </Label>
                <LocationCombobox
                  value={shipping.province_id}
                  items={locations.provinces}
                  placeholder="Pilih provinsi"
                  searchPlaceholder="Cari provinsi..."
                  emptyText="Provinsi tidak ditemukan."
                  loading={loadingLocation === "province"}
                  onChange={(value, item) => pickLocation("province", value, item)}
                />
                {errors.province && <p className={errClass}>{errors.province}</p>}
              </div>

              <div>
                <Label className="text-base text-stone-700">
                  Kota / Kabupaten
                </Label>
                <LocationCombobox
                  value={shipping.city_id}
                  items={locations.cities}
                  disabled={!shipping.province_id}
                  placeholder="Pilih kota / kabupaten"
                  searchPlaceholder="Cari kota / kabupaten..."
                  emptyText="Kota tidak ditemukan."
                  loading={loadingLocation === "city"}
                  onChange={(value, item) => pickLocation("city", value, item)}
                />
                {errors.city && <p className={errClass}>{errors.city}</p>}
              </div>

              <div>
                <Label className="text-base text-stone-700">
                  Kecamatan
                </Label>
                <LocationCombobox
                  value={shipping.district_id}
                  items={locations.districts}
                  disabled={!shipping.city_id}
                  placeholder="Pilih kecamatan"
                  searchPlaceholder="Cari kecamatan..."
                  emptyText="Kecamatan tidak ditemukan."
                  loading={loadingLocation === "district"}
                  onChange={(value, item) => pickLocation("district", value, item)}
                />
                {errors.district && <p className={errClass}>{errors.district}</p>}
              </div>

              <div>
                <Label className="text-base text-stone-700">
                  Kelurahan
                </Label>
                <LocationCombobox
                  value={shipping.subdistrict_id}
                  items={locations.subdistricts}
                  disabled={!shipping.district_id}
                  placeholder="Pilih kelurahan"
                  searchPlaceholder="Cari kelurahan..."
                  emptyText="Kelurahan tidak ditemukan."
                  loading={loadingLocation === "subdistrict"}
                  onChange={(value, item) => pickLocation("subdistrict", value, item)}
                />
                {errors.subdistrict && <p className={errClass}>{errors.subdistrict}</p>}
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="notes" className="text-base text-stone-700">
                  Catatan untuk penjual / kurir <span className="text-stone-400">(opsional)</span>
                </Label>
                <Textarea
                  id="notes"
                  className="min-h-[80px] text-base rounded-xl border-stone-200 focus-visible:ring-emerald-500"
                  value={shipping.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  placeholder="Contoh: Taruh di depan pagar, hubungi sebelum datang"
                  data-testid="input-notes"
                />
              </div>
            </div>
          </div>

          {/* Mobile summary inside the form column */}
          <div className="lg:hidden">
            <CheckoutSummary />
          </div>

          <Button
            type="submit"
            className="w-full h-16 rounded-xl text-lg font-medium bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
            data-testid="shipping-submit"
          >
            Lanjut ke Pengiriman & Pembayaran
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </form>

        {/* Desktop sticky sidebar */}
        <div className="hidden lg:block">
          <div className="sticky top-24">
            <CheckoutSummary />
          </div>
        </div>
      </div>
    </main>
  );
};

export default CheckoutShippingPage;

